import { Form, Layout } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { Delete } from 'app/components/commons/ExtraButtons/Delete/Delete';
import { FeaturesComponent } from 'app/components/commons/Features/Features';
import { BasicInfo } from 'app/components/commons/Room/BasicInfo/BasicInfo';
import { Beds } from 'app/components/commons/Room/Beds/Beds';
import { PicturesComponent } from 'app/components/commons/Room/Pictures/Pictures';
import { UploadList } from 'app/components/commons/Uploader/UploadList/UploadList';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useRoomFeatures } from 'app/hooks/data/rooms/useGetRoomFeatures';
import {
  useDeleteRoom,
  useGetRoom,
  useSaveRoom,
} from 'app/hooks/data/rooms/useRoom';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import type { Bed, Room, RoomFeature } from 'app/typings';
import { getBlockedBathroomFeatures } from 'app/utils/room';

import './RoomDetail.scss';

const { Content } = Layout;

export type FormValues = Partial<Room>;

export const RoomDetail = () => {
  const { id: roomIdParam } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation<Location>();
  const { state } = location;

  const { data: roomFeatures } = useRoomFeatures();

  const { data: fetchedRoom } = useGetRoom(roomIdParam);

  const { data: roomCategories } = useRoomCategories();
  const { mutateAsync: saveRoom } = useSaveRoom();

  const hotelId = fetchedRoom?.hotelId;
  const { mutateAsync: deleteRoom } = useDeleteRoom(hotelId);
  const { data: fetchedHotel } = useGetHotel(hotelId);
  const hotel = fetchedHotel ?? null;
  const roomId = roomIdParam ? parseInt(roomIdParam) : undefined;
  const hotelIdFromSearch = useMemo(() => {
    const parsedHotelId = parseInt(
      new URLSearchParams(location.search).get('hotelId') ?? ''
    );

    return Number.isNaN(parsedHotelId) ? undefined : parsedHotelId;
  }, [location.search]);

  const [mode, setMode] = useState<'view' | 'edit'>(roomId ? 'view' : 'edit');
  const [form] = Form.useForm<FormValues>();

  const basicFeatureIds = useMemo(
    () =>
      roomFeatures
        ?.filter((feature) => feature.basic)
        .map((feature) => feature.id),
    [roomFeatures]
  );

  const categories = useMemo(
    () =>
      roomCategories?.reduce(
        (acc, val) => ({
          ...acc,
          [val.id]: val.name,
        }),
        {} as { [key: number]: string }
      ),
    [roomCategories]
  );

  const featuresByCategory = useMemo(() => {
    return (
      roomFeatures?.reduce((acc: Record<string, RoomFeature[]>, feature) => {
        if (!acc[feature.category]) {
          acc[feature.category] = [];
        }

        acc[feature.category].push(feature);

        return acc;
      }, {}) ?? {}
    );
  }, [roomFeatures]);

  const onCancel = useCallback(() => {
    if (roomId) {
      setMode('view');
    } else {
      history.push('/rooms');
    }
  }, [roomId, history]);

  const room: Partial<Room> | undefined = useMemo(() => {
    // Create form
    if (!roomId) {
      return {};
    }

    // Room stored in redux doesn't match the one we want to edit
    // We don't want to render the form
    if (fetchedRoom && fetchedRoom.id !== roomId) {
      return undefined;
    }

    // Edit form
    return fetchedRoom;
  }, [fetchedRoom, roomId]);

  if (!room || !roomCategories) {
    return null;
  }

  const formattedValues: FormValues = {
    ...room,
    beds: room.beds
      // We want to display the included bed first.
      ?.sort((bedA, bedB) => {
        if (bedA.included && bedB.included && bedA.priority && bedB.priority) {
          return bedA.priority - bedB.priority;
        }

        if (bedA.included) {
          return -1;
        }

        if (bedB.included) {
          return 1;
        }

        return 0;
      })
      .map((bed) => ({
        ...bed,
        included: bed.included,
      })) || [{}],
    featureIds: (room.id ? room.featureIds : basicFeatureIds) ?? undefined,
    hasPRM: room.hasPRM ?? false,
    hotelId: room.hotelId ?? hotelIdFromSearch,
    pictures: room.pictures || [],
  };

  const onFinish = async (values: FormValues) => {
    const formatted = {
      ...values,
      beds: values.beds?.map((bed: Bed) => ({
        ...bed,
        included: bed.included !== false,
        price: bed.price || 0,
      })),
      hasPRM: values.hasPRM !== false,
      id: formattedValues.id,
    } as Room;

    const newRoomId = await saveRoom(formatted);

    history.push({ pathname: `/rooms/legacy/${newRoomId}`, state });

    setMode('view');
  };

  const renderDeleteButton = () => {
    if (!roomId) {
      return null;
    }

    return (
      <Delete
        entityDescription={`Room #${roomId}`}
        onDelete={async () => {
          await deleteRoom(roomId);
          history.push(`/rooms`);
        }}
      />
    );
  };

  const renderTitle = () => {
    if (!formattedValues.id) {
      return 'New room';
    }

    if (formattedValues.name) {
      return (
        <AssetDetailTitle
          assetTitle={formattedValues.name}
          hotelId={hotel?.id}
          hotelName={hotel?.name}
          isNewDataModel={false}
        />
      );
    }

    if (formattedValues.categoryId) {
      return categories?.[formattedValues.categoryId];
    }

    return '';
  };

  return (
    <Layout className="room-detail">
      <FormLegacyRenderProp
        form={form}
        onFinish={onFinish}
        initialValues={formattedValues}
      >
        {(values, { resetFields, isFieldsTouched, getFieldValue }) => (
          <SDetailLayout
            title={renderTitle()}
            mode={mode}
            onEdit={() => setMode('edit')}
            onSave={() => onFinish(values)}
            onCancel={onCancel}
            reset={resetFields}
            headerExtra={renderDeleteButton()}
            isDirty={isFieldsTouched()}
            isValid={form
              .getFieldsError()
              .every((item) => item.errors.length > 0)}
            backRoute={state?.pathname}
          >
            <Content className="room-detail__content">
              <CurrencyWrapper hotel={hotel}>
                <SCard title="Basic information">
                  <BasicInfo
                    hotelSelect={roomId ? 'disabled' : 'enabled'}
                    mode={mode}
                    values={values}
                  />
                </SCard>
                <SCard title="Beds">
                  <Form.List name="beds">
                    {(fields, { add: addBed, remove: removeBed }) => (
                      <Beds
                        fields={fields}
                        add={addBed}
                        remove={removeBed}
                        mode={mode}
                        values={values}
                      />
                    )}
                  </Form.List>
                </SCard>
                <SCard title="Features">
                  <Form.Item name="featureIds">
                    <FeaturesComponent
                      featuresByCategory={featuresByCategory}
                      getDisabledFeatureIds={getBlockedBathroomFeatures}
                      mode={mode}
                    />
                  </Form.Item>
                </SCard>
                <SCard
                  title="Photos"
                  subtitle="In edit mode, you can drag and drop photos to rearrange them. The first one will be the room cover"
                  className="room-detail__last-card"
                >
                  <Form.Item name="pictures">
                    {mode === 'edit' && values.hotelId ? (
                      <PicturesComponent
                        roomCategoryId={room.categoryId}
                        categories={categories ?? []}
                        hotelId={values?.hotelId}
                        picturesCategories={[
                          { name: 'Official', type: 'official' },
                          { name: 'Sourcing', type: 'sourcing' },
                        ]}
                        kind="sourcing"
                        draggable
                        previewable
                        removable
                      />
                    ) : (
                      <UploadList
                        pictures={getFieldValue('pictures')}
                        uploadingItems={{}}
                        previewable
                      />
                    )}
                  </Form.Item>
                </SCard>
              </CurrencyWrapper>
            </Content>
          </SDetailLayout>
        )}
      </FormLegacyRenderProp>
    </Layout>
  );
};
